import React, { useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { LoadResponse } from "../../../../../models/api/load";
import { DTOModel } from "../../../../../models/base/dto-model";
import { EmployeeGroupDTO } from "../../../../../models/employee-groups";
import { TnAWorkedTimeTotalsDTO } from "../../../../../models/tna-worked-time-totals";
import { exportTnAToExcel } from "../../../../../utils/export-presence-and-leaves";
import { getNormaleDateOrMonth, intlFormatDateShort, intlFormatDateWithHour } from "../../../../../utils/time-utils";
import { monthsValues } from "../../../../CustomDropdowns/CustomDropdownMonthsValues/CustomDropdownMonthsValues";
import ExportToExcelDialog from "../../../ExportToExcelDialog/ExportToExcelDialog";
import { MyTnAContext } from "../../../MyTeamPresenceAndLeaves";
import { excelStyles } from "../../../utils";

interface Props {
  disabled: boolean;
  loadEmployeeGroup: () => Promise<EmployeeGroupDTO | undefined>;
  loadTnAWorkedTimeTotalsData: (pageSize?: number) => Promise<LoadResponse<TnAWorkedTimeTotalsDTO> | undefined>;
  allItems: () => DTOModel[];
}

const ExportWorkedTimeTotalsView = ({ disabled, loadEmployeeGroup, loadTnAWorkedTimeTotalsData, allItems }: Props) => {
  const { t } = useTranslation();
  const tRP = "components.team_presence_and_leaves_worked_time";
  const translationFields = `${tRP}.fields`;
  const tRPCommon = "pages.company_account_profile.tables.employees.fields";

  const {
    legalEntity: { legalEntityOption, selectedLegalEntity },
    employeeGroup: { employeeGroupOption },
    currentEmployees: { onlyCurrentEmployeesOptionValue },
    searchingDate: { year, month },
  } = useContext(MyTnAContext);

  const rowsBeforeHeaderInExcel = useCallback(
    (employeeGroup: EmployeeGroupDTO | undefined) => {
      return [
        {
          data: ["Worked time totals"],
          fontSize: 16,
          isBold: true,
        },
        {
          data: [`Report date ${intlFormatDateWithHour(new Date())}`],
          fontSize: 14,
          isBold: false,
          emptyRowsUnder: 1,
        },
        {
          data: [
            `Legal entities: ${!legalEntityOption?.legalEntityIds?.length ? "All" : selectedLegalEntity?.name ?? ""}`,
          ],
          fontSize: 16,
          isBold: true,
          emptyRowsUnder: 1,
        },
        {
          data: [
            `Employee group: ${
              !employeeGroupOption?.employeeGroupIds?.length ? "All groups" : `${employeeGroup?.name ?? ""}`
            }`,
          ],
          fontSize: 12,
          isBold: false,
        },
        {
          data: [
            `Employee status: ${
              onlyCurrentEmployeesOptionValue?.onlyCurrentEmployees ? "Only current employees" : "All employees"
            }`,
          ],
          fontSize: 12,
          isBold: false,
          emptyRowsUnder: 1,
        },
        {
          data: [`Period: ${year} ${t(monthsValues.find((currMonth) => currMonth.value === month)?.key ?? "")}`],
          fontSize: 14,
          isBold: true,
        },
      ];
    },
    [
      legalEntityOption?.legalEntityIds,
      selectedLegalEntity?.name,
      employeeGroupOption,
      onlyCurrentEmployeesOptionValue,
      year,
      month,
    ]
  );

  const headerNames = useMemo(() => {
    return [
      `${t(`${tRPCommon}.employee_code`)}`,
      `${t(`${tRPCommon}.internal_id`)}`,
      `${t("common_words.employee")}`,
      `${t(`${tRPCommon}.birth_date`)}`,
      `${t("common_words.legal_entity")}`,
      `${t("common_words.tna_employee_group")}`,
      `${t(`${translationFields}.due_hours`)}`,
      `${t(`${translationFields}.worked_hours`)}`,
      `${t(`${translationFields}.vacation_hours`)}`,
      `${t(`${translationFields}.paid_leave_hours`)}`,
      `${t(`${translationFields}.business_trip_hours`)}`,
    ];
  }, [year, month]);

  const dataToExportAsExcel = useCallback(
    (allItems: TnAWorkedTimeTotalsDTO[]) => {
      return allItems.map((item) => {
        return {
          [`${t(`${tRPCommon}.employee_code`)}`]: { value: item.employee?.employeeCode ?? " " },
          [`${t(`${tRPCommon}.internal_id`)}`]: { value: item.employee?.internalId ?? " " },
          [`${t("common_words.employee")}`]: {
            value: item.employee?.name ?? " ",
            style: { ...excelStyles.employeeCellStyle },
          },
          [`${t(`${tRPCommon}.birth_date`)}`]: {
            value: item.employee?.birthDate ? intlFormatDateShort(item.employee?.birthDate) : " ",
          },
          [`${t("common_words.legal_entity")}`]: { value: item.employee?.legalEntity?.name ?? " " },
          [`${t("common_words.tna_employee_group")}`]: { value: item?.employee?.tnAGroup?.name ?? " " },

          [`${t(`${translationFields}.due_hours`)}`]: {
            value: `${item?.dueHours ? item?.dueHours.toFixed(2) : " "}`,
            style: { horizontalAlign: "right" },
          },
          [`${t(`${translationFields}.worked_hours`)}`]: {
            value: `${item?.workedTime ? item?.workedTime.toFixed(2) : " "}`,
            style: { horizontalAlign: "right" },
          },
          [`${t(`${translationFields}.vacation_hours`)}`]: {
            value: `${item?.vacationHours ? item?.vacationHours.toFixed(2) : " "}`,
            style: { horizontalAlign: "right" },
          },
          [`${t(`${translationFields}.paid_leave_hours`)}`]: {
            value: `${item?.paidLeaveHours ? item?.paidLeaveHours.toFixed(2) : " "}`,
            style: { horizontalAlign: "right" },
          },
          [`${t(`${translationFields}.business_trip_hours`)}`]: {
            value: `${item?.businessTripHours ? item?.businessTripHours.toFixed(2) : " "}`,
            style: { horizontalAlign: "right" },
          },
          sortDirection: headerNames,
        };
      });
    },
    [headerNames, year, month]
  );

  const exportToExcel = useCallback(
    async (option: "exportAllData" | "exportCurrent" = "exportCurrent") => {
      if (option === "exportCurrent") {
        const employeeGroup = employeeGroupOption?.employeeGroupIds?.length ? await loadEmployeeGroup() : undefined;

        exportTnAToExcel(
          dataToExportAsExcel(allItems() as TnAWorkedTimeTotalsDTO[]),
          headerNames as string[],
          `${new Date().getFullYear()} ${getNormaleDateOrMonth(new Date().getMonth() + 1)} ${getNormaleDateOrMonth(
            new Date().getDate()
          )} ${!legalEntityOption?.legalEntityIds?.length ? "All legal entities" : selectedLegalEntity?.name ?? ""} ${
            !employeeGroupOption?.employeeGroupIds?.length ? "All groups" : `${employeeGroup?.name ?? ""}`
          } Worked time totals`,
          rowsBeforeHeaderInExcel(employeeGroup)
        );
      }

      if (option === "exportAllData") {
        const employeeGroup = employeeGroupOption?.employeeGroupIds?.length ? await loadEmployeeGroup() : undefined;

        const firstResponse = await loadTnAWorkedTimeTotalsData();

        if (firstResponse?.error) return;

        const allTnAWorkedTimeData = await loadTnAWorkedTimeTotalsData(firstResponse?.totalItems);

        if (!!allTnAWorkedTimeData?.error || !allTnAWorkedTimeData?.items) return;

        exportTnAToExcel(
          dataToExportAsExcel(allTnAWorkedTimeData.items),
          headerNames as string[],
          `${new Date().getFullYear()} ${getNormaleDateOrMonth(new Date().getMonth() + 1)} ${getNormaleDateOrMonth(
            new Date().getDate()
          )} ${!legalEntityOption?.legalEntityIds?.length ? "All legal entities" : selectedLegalEntity?.name ?? ""} ${
            !employeeGroupOption?.employeeGroupIds?.length ? "All groups" : `${employeeGroup?.name ?? ""}`
          } Worked time totals`,
          rowsBeforeHeaderInExcel(employeeGroup)
        );
      }
    },
    [
      rowsBeforeHeaderInExcel,
      loadEmployeeGroup,
      headerNames,
      dataToExportAsExcel,
      employeeGroupOption?.employeeGroupIds,
      loadTnAWorkedTimeTotalsData,
    ]
  );

  return (
    <ExportToExcelDialog
      handleExportToExcel={exportToExcel}
      disabled={disabled}
    />
  );
};

export default ExportWorkedTimeTotalsView;
